// Base
import * as Yup from "yup";
import valid from "card-validator";

// Types
import type {
  FormErrors,
  CheckoutFormValues,
  FormTypeContext,
} from "@/types/types";

const isExpirationValid = (month: string, year: string) => {
  if (!month || !year) return false;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const expYear = parseInt(year, 10);
  const expMonth = parseInt(month, 10);

  if (expYear < currentYear) return false;
  if (expYear === currentYear && expMonth < currentMonth) return false;
  return expYear <= currentYear + 10;
};

const symbolRegex =
  /^(?:[\u0000-\u007F\u0080-\u00FF\u0100-\u017F\u0180-\u024F\u0400-\u04FF\u4E00-\u9FFF\u0600-\u06FF])+$/u;

export const createValidationSchema = (form: { errors: FormErrors }) =>
  Yup.object<CheckoutFormValues>().shape({
    email: Yup.string()
      .email(form.errors.tr_email_error)
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),

    phone: Yup.string().when("$type", (type: FormTypeContext[], schema) => {
      if (type[0].method === "card" || type[0].paypalHandler === "wr") {
        return schema
          .test("phoneValidTest", form.errors.tr_phone_error, () => {
            const input = document.querySelector(
              ".iti__tel-input",
            ) as HTMLInputElement;
            const iti = (window as any).intlTelInputGlobals.getInstance(input);
            return iti?.isValidNumber?.();
          })
          .test(
            "symbolValidTest",
            form.errors.tr_symbol_error,
            (value) => !value || symbolRegex.test(value),
          )
          .required(form.errors.tr_required_error);
      }
      return schema;
    }),

    first_name: Yup.string()
      .matches(/^\D*$/, form.errors.tr_symbol_error)
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),

    last_name: Yup.string()
      .matches(/^\D*$/, form.errors.tr_symbol_error)
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),

    // Address, country, state, zip, city
    address: Yup.string()
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),
    country: Yup.string()
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),
    state: Yup.string()
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),
    zip: Yup.string()
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),
    city: Yup.string()
      .test(
        "symbolValidTest",
        form.errors.tr_symbol_error,
        (value) => !value || symbolRegex.test(value),
      )
      .when("$type", (type: FormTypeContext[], schema) => {
        if (type[0].method === "card" || type[0].paypalHandler === "wr") {
          return schema.required(form.errors.tr_required_error);
        }
        return schema;
      }),

    // Card fields
    card_name: Yup.string().when("$type", (type: FormTypeContext[], schema) => {
      if (type[0].method === "card") {
        return schema.required(form.errors.tr_required_error);
      }
      return schema;
    }),

    card_number: Yup.string().when(
      "$type",
      (type: FormTypeContext[], schema) => {
        if (type[0].method === "card") {
          return schema
            .test("test-cardNumb", form.errors.tr_card_error, (value) => {
              const cardInfo = valid.number(value);
              return (
                cardInfo.isValid ||
                value === "0000 0000 0000 0000" ||
                value === "0000 0000 0111 1111" ||
                value === "7111 1111 1110 0000"
              );
            })
            .required(form.errors.tr_required_error);
        }
        return schema;
      },
    ),

    card_cvc: Yup.string().when("$type", (type: FormTypeContext[], schema) => {
      if (type[0].method === "card") {
        return schema
          .test("test-cvvNumb", form.errors.tr_cvv_error, function (value) {
            const card_number = this.parent.card_number;
            const cvvLength = valid.number(card_number).card?.code?.size;
            return (
              valid.cvv(value, cvvLength).isValid ||
              card_number === "0000 0000 0000 0000"
            );
          })
          .required(form.errors.tr_required_error);
      }
      return schema;
    }),

    card_exp_year: Yup.string().when(
      "$type",
      (type: FormTypeContext[], schema) => {
        if (type[0].method === "card") {
          return schema
            .required(form.errors.tr_required_error)
            .test(
              "test-exp_year",
              form.errors.tr_expiration_error,
              function (value) {
                if (!valid.expirationYear(value).isValid) return false;
                const month = this.parent.card_exp_month;
                return isExpirationValid(month, value);
              },
            );
        }
        return schema;
      },
    ),

    card_exp_month: Yup.string().when(
      "$type",
      (type: FormTypeContext[], schema) => {
        if (type[0].method === "card") {
          return schema
            .required(form.errors.tr_required_error)
            .test(
              "test-exp_month",
              form.errors.tr_expiration_error,
              function (value) {
                if (!valid.expirationMonth(value).isValid) return false;
                const year = this.parent.card_exp_year;
                return isExpirationValid(value, year);
              },
            );
        }
        return schema;
      },
    ),
  });
